import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { 
  ShoppingCart, Eye, Phone, Truck, Printer, RefreshCw, Package, 
  MapPin, Calendar, Clock, User, Building2, DollarSign, Edit, 
  CheckCircle, XCircle, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import Invoice from "../../components/Invoice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const { getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [editingDeliveryFee, setEditingDeliveryFee] = useState(null);
  const [newDeliveryFee, setNewDeliveryFee] = useState("");

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const headers = getAuthHeaders();
      const [ordersRes, driversRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/drivers`, { headers }),
      ]);
      setOrders(ordersRes.data);
      setDrivers(driversRes.data);
      if (showRefreshing) toast.success(isRTL ? "تم التحديث" : "Refreshed");
    } catch (error) {
      toast.error(t("failedToLoadOrders"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, t, isRTL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const updateStatus = async (orderId, status, driverId = null) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status, driver_id: driverId }, { headers: getAuthHeaders() });
      toast.success(t("orderUpdated"));
      fetchData();
    } catch (error) {
      toast.error(t("failedToUpdateOrder"));
    }
  };

  const updateDeliveryFee = async (orderId) => {
    if (!newDeliveryFee || isNaN(parseFloat(newDeliveryFee))) {
      toast.error(isRTL ? "الرجاء إدخال مبلغ صحيح" : "Please enter a valid amount");
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}/delivery-fee`, 
        { delivery_fee: parseFloat(newDeliveryFee) }, 
        { headers: getAuthHeaders() }
      );
      toast.success(isRTL ? "تم تحديث رسوم التوصيل" : "Delivery fee updated");
      setEditingDeliveryFee(null);
      setNewDeliveryFee("");
      fetchData();
    } catch (error) {
      toast.error(isRTL ? "فشل في تحديث رسوم التوصيل" : "Failed to update delivery fee");
    }
  };

  const assignDriver = async (orderId, driverId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/assign-driver`, 
        { driver_id: driverId }, 
        { headers: getAuthHeaders() }
      );
      toast.success(isRTL ? "تم تعيين السائق" : "Driver assigned");
      fetchData();
    } catch (error) {
      toast.error(isRTL ? "فشل في تعيين السائق" : "Failed to assign driver");
    }
  };

  const openInvoice = (order) => {
    setInvoiceOrder(order);
    setInvoiceOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      processing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      out_for_delivery: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      delivered: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
      returned: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    return colors[status] || "bg-slate-500/20 text-slate-400";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      out_for_delivery: Truck,
      delivered: CheckCircle,
      cancelled: XCircle,
      returned: AlertCircle,
    };
    return icons[status] || Clock;
  };

  const filteredOrders = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const getWhatsAppLink = (phone, order) => {
    const msg = encodeURIComponent(isRTL ? `مرحباً، بخصوص الطلب رقم #${order.id.slice(0, 8)}` : `Hi, regarding order #${order.id.slice(0, 8)}`);
    return `https://wa.me/${phone?.replace(/\D/g, "")}?text=${msg}`;
  };

  const statusLabels = {
    all: t("all"),
    pending: t("pending"),
    processing: t("processing"),
    out_for_delivery: t("outForDelivery"),
    delivered: t("delivered"),
    cancelled: t("cancelled"),
    returned: isRTL ? "مرتجع" : "Returned",
  };

  const getDeliveryTypeLabel = (type) => {
    if (type === "pickup") return isRTL ? "استلام من المخزن" : "Pickup";
    return isRTL ? "توصيل" : "Delivery";
  };

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    processing: orders.filter(o => o.status === "processing").length,
    out_for_delivery: orders.filter(o => o.status === "out_for_delivery").length,
    delivered: orders.filter(o => o.status === "delivered").length,
    totalRevenue: orders.filter(o => o.status === "delivered").reduce((sum, o) => sum + (o.total || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      {/* Header with Refresh */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-white">{t("orders")}</h2>
          <p className="text-slate-400 text-sm">{isRTL ? "إدارة جميع الطلبات" : "Manage all orders"}</p>
        </div>
        <Button 
          onClick={() => fetchData(true)} 
          disabled={refreshing}
          className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90"
          data-testid="refresh-orders-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? (isRTL ? "جاري التحديث..." : "Refreshing...") : (isRTL ? "تحديث" : "Refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-slate-400">{isRTL ? "إجمالي الطلبات" : "Total Orders"}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
            <p className="text-xs text-yellow-400/70">{t("pending")}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">{stats.processing}</p>
            <p className="text-xs text-blue-400/70">{t("processing")}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-400">{stats.out_for_delivery}</p>
            <p className="text-xs text-purple-400/70">{t("outForDelivery")}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{stats.delivered}</p>
            <p className="text-xs text-green-400/70">{t("delivered")}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#d4af37]/10 border-[#d4af37]/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#d4af37]">{stats.totalRevenue.toFixed(0)}</p>
            <p className="text-xs text-[#d4af37]/70">{t("lyd")} {isRTL ? "الإيرادات" : "Revenue"}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "processing", "out_for_delivery", "delivered", "cancelled"].map((s) => (
          <Button 
            key={s} 
            variant={filter === s ? "default" : "outline"} 
            size="sm" 
            onClick={() => setFilter(s)}
            className={filter === s ? "bg-[#d4af37] text-[#0f172a]" : "border-slate-700 text-slate-300 hover:bg-slate-700"}
          >
            {statusLabels[s]}
            {s !== "all" && <Badge className="ml-2 bg-slate-600 text-white text-xs">{orders.filter(o => o.status === s).length}</Badge>}
          </Button>
        ))}
      </div>

      {/* Orders Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
              {t("loading")}
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">{t("noOrders")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">{t("order")}</TableHead>
                    <TableHead className="text-slate-300">{t("customer")}</TableHead>
                    <TableHead className="text-slate-300">{isRTL ? "المنتجات" : "Items"}</TableHead>
                    <TableHead className="text-slate-300">{t("total")}</TableHead>
                    <TableHead className="text-slate-300">{isRTL ? "رسوم التوصيل" : "Delivery Fee"}</TableHead>
                    <TableHead className="text-slate-300">{t("status")}</TableHead>
                    <TableHead className="text-slate-300">{t("driver")}</TableHead>
                    <TableHead className="text-slate-300 text-right">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const StatusIcon = getStatusIcon(order.status);
                    return (
                      <TableRow key={order.id} className="border-slate-700 hover:bg-slate-700/30">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${order.status === 'pending' ? 'bg-yellow-400 animate-pulse' : 'bg-slate-500'}`} />
                            <div>
                              <p className="text-white font-medium">#{order.id.slice(0, 8)}</p>
                              <p className="text-xs text-slate-400 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(order.created_at).toLocaleDateString(isRTL ? 'ar-LY' : 'en-US')}
                              </p>
                              <Badge variant="outline" className="text-xs mt-1 border-slate-600">
                                {getDeliveryTypeLabel(order.delivery_type)}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-white font-medium flex items-center gap-1">
                              <User className="h-3 w-3 text-slate-400" />
                              {order.customer_name}
                            </p>
                            {order.customer_company && (
                              <p className="text-xs text-slate-400 flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {order.customer_company}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {order.customer_phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[150px]">
                            <p className="text-white text-sm">{order.items?.length || 0} {isRTL ? "منتجات" : "items"}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {order.items?.slice(0, 2).map(i => i.product_name).join(", ")}
                              {order.items?.length > 2 && "..."}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-white font-bold">{order.total?.toFixed(2)} {t("lyd")}</p>
                          <p className="text-xs text-slate-400">{isRTL ? "المجموع الفرعي" : "Subtotal"}: {order.subtotal?.toFixed(2)}</p>
                        </TableCell>
                        <TableCell>
                          {editingDeliveryFee === order.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={newDeliveryFee}
                                onChange={(e) => setNewDeliveryFee(e.target.value)}
                                className="w-20 h-8 bg-slate-700 border-slate-600 text-white text-sm"
                                placeholder="0"
                              />
                              <Button size="sm" className="h-8 bg-green-600" onClick={() => updateDeliveryFee(order.id)}>
                                <CheckCircle className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingDeliveryFee(null)}>
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`${order.delivery_fee > 0 ? 'text-white' : 'text-slate-500'}`}>
                                {order.delivery_fee > 0 ? `${order.delivery_fee} ${t("lyd")}` : (isRTL ? "غير محدد" : "Not set")}
                              </span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                                onClick={() => { setEditingDeliveryFee(order.id); setNewDeliveryFee(order.delivery_fee?.toString() || ""); }}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v, order.driver_id)}>
                            <SelectTrigger className={`w-40 ${getStatusColor(order.status)} border`}>
                              <div className="flex items-center gap-2">
                                <StatusIcon className="h-4 w-4" />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600">
                              {["pending", "processing", "out_for_delivery", "delivered", "cancelled"].map((s) => (
                                <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={order.driver_id || "unassigned"} 
                            onValueChange={(v) => v !== "unassigned" && assignDriver(order.id, v)}
                          >
                            <SelectTrigger className="w-36 bg-slate-700 border-slate-600">
                              <SelectValue placeholder={isRTL ? "تعيين سائق" : "Assign"} />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600">
                              <SelectItem value="unassigned" disabled>{isRTL ? "اختر سائق" : "Select driver"}</SelectItem>
                              {drivers.map((d) => (
                                <SelectItem key={d.id} value={d.id}>
                                  <div className="flex items-center gap-2">
                                    <Truck className="h-3 w-3" />
                                    {d.full_name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {order.driver_name && (
                            <p className="text-xs text-slate-400 mt-1">{order.driver_name}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => { setSelectedOrder(order); setDialogOpen(true); }} 
                              className="text-slate-400 hover:text-white h-8 w-8"
                              data-testid={`view-order-${order.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openInvoice(order)} 
                              className="text-blue-400 hover:text-blue-300 h-8 w-8"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <a href={getWhatsAppLink(order.customer_phone, order)} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300 h-8 w-8">
                                <Phone className="h-4 w-4" />
                              </Button>
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Package className="h-5 w-5 text-[#d4af37]" />
              {t("order")} #{selectedOrder?.id.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={`${getStatusColor(selectedOrder.status)} px-3 py-1`}>
                  {statusLabels[selectedOrder.status]}
                </Badge>
                <span className="text-sm text-slate-400">
                  {new Date(selectedOrder.created_at).toLocaleString(isRTL ? 'ar-LY' : 'en-US')}
                </span>
              </div>

              {/* Customer Info Card */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4 text-[#d4af37]" />
                    {isRTL ? "معلومات العميل" : "Customer Information"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-400">{t("name")}</Label>
                    <p className="text-white">{selectedOrder.customer_name}</p>
                  </div>
                  {selectedOrder.customer_company && (
                    <div>
                      <Label className="text-slate-400">{isRTL ? "الشركة" : "Company"}</Label>
                      <p className="text-white">{selectedOrder.customer_company}</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-400">{t("phone")}</Label>
                    <p className="text-white">{selectedOrder.customer_phone}</p>
                  </div>
                  <div>
                    <Label className="text-slate-400">{isRTL ? "نوع التسليم" : "Delivery Type"}</Label>
                    <p className="text-white">{getDeliveryTypeLabel(selectedOrder.delivery_type)}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-400">{t("address")}</Label>
                    <p className="text-white flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                      {selectedOrder.customer_address}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Order Items */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-[#d4af37]" />
                    {isRTL ? "المنتجات المطلوبة" : "Order Items"} ({selectedOrder.items?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, i) => (
                      <div key={i} className="flex justify-between items-center py-3 border-b border-slate-600 last:border-0">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.product_name}</p>
                          <p className="text-sm text-slate-400">
                            {item.price} {t("lyd")} × {item.quantity}
                          </p>
                        </div>
                        <p className="text-white font-bold">{item.total?.toFixed(2)} {t("lyd")}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Order Totals */}
                  <div className="mt-4 pt-4 border-t border-slate-600 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{isRTL ? "المجموع الفرعي" : "Subtotal"}</span>
                      <span className="text-white">{selectedOrder.subtotal?.toFixed(2)} {t("lyd")}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{isRTL ? "رسوم التوصيل" : "Delivery Fee"}</span>
                      <span className="text-white">
                        {selectedOrder.delivery_fee > 0 ? `${selectedOrder.delivery_fee} ${t("lyd")}` : (isRTL ? "غير محدد" : "Not set")}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-600">
                      <span className="text-white">{t("total")}</span>
                      <span className="text-[#d4af37]">{selectedOrder.total?.toFixed(2)} {t("lyd")}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Driver Info */}
              {selectedOrder.driver_name && (
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Truck className="h-5 w-5 text-[#d4af37]" />
                    <div>
                      <p className="text-sm text-slate-400">{isRTL ? "السائق المعين" : "Assigned Driver"}</p>
                      <p className="text-white font-medium">{selectedOrder.driver_name}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-400 mb-1">{t("notes")}</p>
                    <p className="text-white">{selectedOrder.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={() => openInvoice(selectedOrder)} className="flex-1 bg-[#d4af37] text-[#0f172a]">
                  <Printer className="w-4 h-4 mr-2" /> {t("printInvoice")}
                </Button>
                <a href={getWhatsAppLink(selectedOrder.customer_phone, selectedOrder)} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    <Phone className="w-4 h-4 mr-2" /> {isRTL ? "واتساب" : "WhatsApp"}
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Invoice
        isOpen={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        order={invoiceOrder}
        customer={{ full_name: invoiceOrder?.customer_name, phone: invoiceOrder?.customer_phone, address: invoiceOrder?.customer_address }}
      />
    </div>
  );
};

export default AdminOrders;
